import { z } from "zod";

/**
 * Phase 8d — wire schema for R-AI-4 nameplate-OCR responses. The AI
 * reads a single hardwired-appliance nameplate (water heater,
 * furnace, AC condenser, EV charger, …) and proposes the
 * `make` / `model` / electrical-spec fields without manual
 * transcription.
 *
 * One nameplate OCR call produces one `SuggestionSet`. The natural
 * cardinality is one suggestion per *field* the model can read, so
 * the user can accept/edit/reject each independently — a model
 * confident in `manufacturer` but unsure of `mainAmperage` should
 * surface that asymmetry rather than collapsing both into a single
 * accept-everything decision. The set may also be a single
 * combined proposal when the model returns the nameplate as one
 * blob; both shapes are legal under one wire schema.
 *
 * Per the suggestion-set arms documented in `suggestion.ts`,
 * `nameplate-ocr` is trivially singleton in the typical case; the
 * iteration-order contract (R-AI-2a) still applies — emit fields in
 * a stable order so the review UI walks them deterministically.
 *
 * The apply step (`applyNameplateOcr`) merges accepted/edited values
 * onto a target device's nameplate; the existing device's `nameplate`
 * (if any) is the merge base. Per-field semantics: an accepted
 * proposal overwrites the corresponding field; a rejected proposal
 * leaves the field at its prior value; a `pending` proposal is the
 * same as a reject for apply purposes (the caller is expected to
 * bulk-accept-undecided before applying).
 */

/**
 * One nameplate-field proposal. `field` discriminates which slot on
 * `Nameplate` the value targets; `value` is the model's read of that
 * field. The schema admits arbitrary non-empty strings for the four
 * `string` fields and a positive integer for `voltage` /
 * `amperage` / `wattage` numeric fields when the model recovers them.
 *
 * The supported field list intentionally mirrors `Nameplate` and adds
 * the three electrical-spec numeric fields (`voltage`, `amperage`,
 * `wattage`) per R-AI-4's "electrical specs" phrasing. The `Nameplate`
 * type itself does not carry those numeric fields today; the apply
 * step routes the numeric suggestions into a `nameplateExtras` map
 * on the result (see `applyNameplateOcr`) so callers can read them
 * without forcing a `Nameplate` shape extension in this slice.
 *
 * `kind` discriminates the proposal arm. Reserved for the future
 * "whole nameplate blob" arm if a later model emits a single
 * structured object; for Phase 8d the per-field shape is the canonical
 * one.
 */
const stringFieldSchema = z.object({
  kind: z.literal("string"),
  field: z.enum(["manufacturer", "model", "serial", "year"]),
  value: z.string().min(1),
});

const numericFieldSchema = z.object({
  kind: z.literal("numeric"),
  field: z.enum(["voltage", "amperage", "wattage"]),
  value: z.number().int().positive(),
});

export const nameplateOcrProposalSchema = z.discriminatedUnion("kind", [
  stringFieldSchema,
  numericFieldSchema,
]);

export type NameplateOcrProposal = z.infer<typeof nameplateOcrProposalSchema>;
export type NameplateOcrStringField = z.infer<typeof stringFieldSchema>;
export type NameplateOcrNumericField = z.infer<typeof numericFieldSchema>;
export type NameplateOcrStringKey = NameplateOcrStringField["field"];
export type NameplateOcrNumericKey = NameplateOcrNumericField["field"];
